import debounce from 'lodash/debounce';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

const RESIZE_DEBOUNCE_MS = 200;

export const useMaxTabHeight = (
  activeIndex: number,
  ready: boolean,
  tabCount = 0,
  // Selector of the tab content wrapper to measure — the layout editor tabs
  // carry their own class.
  wrapperSelector = '.form-section-tab-content-wrapper'
) => {
  const [minPanelHeight, setMinPanelHeight] = useState(0);
  const tabViewRef = useRef<any>(null);

  // Measure only once the active tab's inputs finished rendering (ready), so
  // the height reflects fully-laid-out content, not the loading spinner.
  // renderActiveOnly={false} keeps panels mounted, so also observe the active
  // (non-hidden) panel's wrapper to re-measure on later size changes / resize.
  useLayoutEffect(() => {
    if (!ready) return;
    const root = tabViewRef.current?.getElement() as HTMLElement | null;
    const wrapper = root?.querySelector(
      `.p-tabview-panel:not(.p-hidden) ${wrapperSelector}`
    ) as HTMLElement | null;
    if (!wrapper) return;

    const measure = () =>
      setMinPanelHeight((prev) => Math.max(prev, wrapper.offsetHeight));
    measure();

    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(wrapper);
    return () => resizeObserver.disconnect();
  }, [activeIndex, ready, tabCount, wrapperSelector]);

  // Reset on window resize so the tallest-visited height can shrink; clearing
  // minHeight reflows the wrapper, the ResizeObserver above then re-measures.
  useEffect(() => {
    const onResize = debounce(() => setMinPanelHeight(0), RESIZE_DEBOUNCE_MS);
    window.addEventListener('resize', onResize);
    return () => {
      onResize.cancel();
      window.removeEventListener('resize', onResize);
    };
  }, []);

  return { tabViewRef, minPanelHeight };
};
